import React, { useCallback, useEffect, useRef, useState } from "react";
import "./saturationBrightnessPanel.scss";
import { clamp } from "lodash";
import { calculateRgbFromHsv, IColorPickerHsv, IColorPickerRgb } from "../../colorPickerHelpers";
import { ColorHandle } from "./ColorHandle";
import { HUE_SPECTRUM_HEIGHT } from "./HueSpectrum";

interface ISaturationBrightnessPanelProps {
  rgb: IColorPickerRgb;
  hsv: IColorPickerHsv;
  dispatchUpdateHsv: (updatedHsv: IColorPickerHsv) => void;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}

interface ISaturationBrightnessPanelHandle {
  x: number;
  y: number;
  moving: boolean;
}

const PANEL_HEIGHT = 200;

export function SaturationBrightnessPanel(props: ISaturationBrightnessPanelProps) {
  const { rgb, hsv, dispatchUpdateHsv, onGestureStart, onGestureEnd } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const gradientGridRef = useRef<IColorPickerHsv[][]>([]);
  const [handle, setHandle] = useState<ISaturationBrightnessPanelHandle>({ x: 0, y: 0, moving: false });

  function clampHandleX(x: number) {
    const canvasBoundingRect = canvasRef.current?.getBoundingClientRect();
    if (x == null || !canvasBoundingRect) {
      return 0;
    }
    return clamp(x, 0, canvasBoundingRect.width);
  }

  function clampHandleY(y: number) {
    const canvasBoundingRect = canvasRef.current?.getBoundingClientRect();
    if (y == null || !canvasBoundingRect) {
      return 0;
    }
    return clamp(y, 0, canvasBoundingRect.height);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) {
      return;
    }

    canvas.width = canvas.getBoundingClientRect().width;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const imageData = ctx.createImageData(canvas.width, canvas.height);
    const data = imageData.data;
    gradientGridRef.current = [];

    for (let y = 0; y <= canvas.height; y++) {
      const v = 100 - (y / canvas.height) * 100;
      const gradientRow: IColorPickerHsv[] = [];
      for (let x = 0; x <= canvas.width; x++) {
        const s = (x / canvas.width) * 100;
        const i = (y * canvas.width + x) * 4;
        const pixelHsv: IColorPickerHsv = { h: hsv.h, s, v };
        const pixelRgb = calculateRgbFromHsv(pixelHsv);
        data[i] = pixelRgb.r;
        data[i + 1] = pixelRgb.g;
        data[i + 2] = pixelRgb.b;
        data[i + 3] = 255;
        gradientRow.push(pixelHsv);
      }
      gradientGridRef.current.push(gradientRow);
    }

    ctx.putImageData(imageData, 0, 0);
  }, [hsv.h]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (handle.moving || !canvas) {
      return;
    }
    const x = clampHandleX((hsv.s / 100) * canvas.width);
    const y = clampHandleY((1 - hsv.v / 100) * canvas.height);
    setHandle(prev => ({ ...prev, x, y }));
  }, [handle.moving, hsv.s, hsv.v]);

  const updateHsv = useCallback(
    (clientX: number, clientY: number) => {
      const canvasBoundingRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasBoundingRect) {
        return;
      }
      const x = clampHandleX(clientX - canvasBoundingRect.x);
      const y = clampHandleY(clientY - canvasBoundingRect.y);
      setHandle(prev => ({ ...prev, x, y }));
      const hsvMatch = gradientGridRef.current[Math.round(y)]?.[Math.round(x)];
      if (!hsvMatch) {
        return;
      }
      dispatchUpdateHsv({ ...hsvMatch });
    },
    [dispatchUpdateHsv],
  );

  const handlePointerMove = useCallback((e: PointerEvent) => updateHsv(e.clientX, e.clientY), [updateHsv]);
  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      if (canvasRef.current?.hasPointerCapture(e.pointerId)) {
        canvasRef.current.releasePointerCapture(e.pointerId);
      }
      handleRef.current?.releasePointerCapture(e.pointerId);
      setHandle(prev => ({ ...prev, moving: false }));
      onGestureEnd?.();
    },
    [onGestureEnd],
  );

  useEffect(() => {
    if (handle.moving) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handle.moving, handlePointerMove, handlePointerUp]);

  const onHandlePointerDown: React.PointerEventHandler<HTMLDivElement> = e => {
    if (e.button !== 0) {
      return;
    }
    if (handleRef.current) {
      onGestureStart?.();
      handleRef.current.setPointerCapture(e.pointerId);
      setHandle(prev => ({ ...prev, moving: true }));
    }
  };

  const onCanvasPointerDown: React.PointerEventHandler<HTMLCanvasElement> = e => {
    if (e.button !== 0) {
      return;
    }
    onGestureStart?.();
    e.currentTarget.setPointerCapture(e.pointerId);
    updateHsv(e.clientX, e.clientY);
    setHandle(prev => ({ ...prev, moving: true }));
  };

  return (
    <div className="saturation-brightness-panel-container">
      <canvas
        ref={canvasRef}
        className="saturation-brightness-panel"
        height={PANEL_HEIGHT}
        onPointerDown={onCanvasPointerDown}
      />
      <ColorHandle
        ref={handleRef}
        x={handle.x}
        y={handle.y}
        height={HUE_SPECTRUM_HEIGHT}
        rgb={rgb}
        moving={handle.moving}
        onPointerDown={onHandlePointerDown}
      />
    </div>
  );
}
